import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LanguageSwitcher from './components/LanguageSwitcher';

const LandingPage = () => {
  // 动画效果状态
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    setIsVisible(true);
    
    // 检测是否为移动端
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // 定义报告模板数据
  const reportTemplates = [
    {
      id: 'student-profile',
      title: 'Student Profile',
      description: 'Comprehensive student assessment and personalized learning insights',
      image: (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative p-5 bg-white ring-1 ring-gray-200 rounded-lg overflow-hidden flex items-center justify-center transform transition duration-500 group-hover:scale-105">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
          </div>
        </div>
      ),
      path: '',
      features: ['Academic Performance', 'Learning Style Analysis', 'Strengths & Areas for Growth', 'Goal Setting']
    },
    {
      id: 'subject-analysis',
      title: 'Subject Analysis',
      description: 'In-depth subject performance analysis with data-driven insights',
      image: (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative p-5 bg-white ring-1 ring-gray-200 rounded-lg overflow-hidden flex items-center justify-center transform transition duration-500 group-hover:scale-105">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
          </div>
        </div>
      ),
      path: '', 
      features: ['Subject Mastery Tracking', 'Performance Trends', 'Skill Gap Analysis', 'Improvement Recommendations']
    },
    {
      id: 'future-planning',
      title: 'Future Planning',
      description: 'Strategic academic and career pathway planning with AI recommendations',
      image: (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative p-5 bg-white ring-1 ring-gray-200 rounded-lg overflow-hidden flex items-center justify-center transform transition duration-500 group-hover:scale-105">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998a12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
            </svg>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 to-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
          </div>
        </div>
      ),
      path: '',
      features: ['University Preparation', 'Career Guidance', 'Study Abroad Planning', 'Scholarship Opportunities']
    }
  ];

  return (
    <>
      <style>
        {`
          * {
            font-family: 'Comfortaa', 'SF Pro Display', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          }
          
          /* Memphis Design Style Variables */
          :root {
            --memphis-blue: #003366;
            --memphis-cyan: #339FFF;
            --memphis-yellow: #FFD700;
            --memphis-black: #000000;
            --memphis-shadow: 8px 8px 0px 0px;
          }
          
          /* Memphis geometric decorations */
          .memphis-decoration-1 {
            position: absolute;
            top: 10%;
            right: 10%;
            width: 60px;
            height: 60px;
            background: linear-gradient(45deg, #FFD700, #FF6B6B);
            transform: rotate(45deg);
            z-index: 1;
          }
          
          .memphis-decoration-2 {
            position: absolute;
            bottom: 15%;
            left: 5%;
            width: 40px;
            height: 80px;
            background: #339FFF;
            transform: rotate(-15deg);
            z-index: 1;
          }
          
          .memphis-decoration-3 {
            position: absolute;
            top: 30%;
            left: 8%;
            width: 50px;
            height: 50px;
            background: #FFD700;
            border-radius: 50%;
            z-index: 1;
          }
          
          /* Memphis typography effects */
          .memphis-text-shadow {
            text-shadow: 3px 3px 0px #000000;
          }
          
          .memphis-outline-text {
            -webkit-text-stroke: 2px #000000;
            -webkit-text-fill-color: transparent;
          }
          
          /* Comfortaa font class */
          .font-comfortaa {
            font-family: 'Comfortaa', 'SF Pro Display', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important;
          }
          
          /* Memphis style animations */
          @keyframes memphis-bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }
          
          .memphis-bounce {
            animation: memphis-bounce 2s infinite;
          }
          
          @keyframes memphis-rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          
          .memphis-rotate {
            animation: memphis-rotate 20s linear infinite;
          }
        `}
      </style>
      <div className="min-h-screen bg-white overflow-hidden" style={{ fontFamily: "'Comfortaa', 'SF Pro Display', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', system-ui, -apple-system, BlinkMacSystemFont, sans-serif" }}>
        {/* 沉浸式首屏区域 */}
        <div 
          className="relative min-h-screen"
          style={{
            backgroundImage: 'url(https://canada-public.oss-cn-shenzhen.aliyuncs.com/pub_20250529/33e762b5fc984dc9b216017dfc3bd024.jpg)',
            backgroundSize: isMobile ? 'cover' : '100% auto',
            backgroundPosition: isMobile ? 'center center' : 'center 20%',
            backgroundRepeat: 'no-repeat',
            backgroundColor: '#6BB6FF'
          }}
        >
          {/* 渐变遮罩 */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-blue-800/20 to-purple-900/30"></div>
          
          {/* 透明导航栏 */}
          <nav className="relative z-50 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center">
              <div className="flex items-center">
                  {/* Logo */}
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <img 
                        src="https://canada-public.oss-cn-shenzhen.aliyuncs.com/pub_20250529/d9205bc755c6427f9a87e2280c7b8f98.png" 
                        alt="SDP Logo" 
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                    <div className="text-white">
                      <span className="text-2xl font-bold tracking-tight">SDP</span>
                      <div className="text-xs opacity-80 tracking-wider">AlphaEdu.AI</div>
                    </div>
                  </div>
                </div>
                
                {/* Navigation Menu */}
                <div className="hidden md:flex items-center space-x-8">
                  <Link
                    to="/admission-probability"
                    className="px-4 py-3 text-white font-bold tracking-wide rounded-lg border border-white/35 backdrop-blur-sm transition-all duration-300 hover:bg-white/15"
                  >
                    Admissions
                  </Link>
                  <a 
                    href="https://forms.gle/wWA4FyPa1mw2dM9g9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-black uppercase tracking-wider rounded-lg border border-white/20 backdrop-blur-sm shadow-lg shadow-yellow-500/25 transition-all duration-300 hover:scale-105 transform"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="relative z-10 flex items-start justify-center min-h-screen pt-20 md:pt-32 -mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              {/* Left-aligned Content with 科技感背景卡片 */}
              <div className="flex justify-start">
                <div 
                  className="text-white max-w-4xl w-full"
                  style={{ 
                    opacity: isVisible ? 1 : 0, 
                    transform: isVisible ? 'translateY(0)' : 'translateY(50px)', 
                    transition: 'opacity 1.2s ease-out, transform 1.2s ease-out' 
                  }}
                >
                  {/* 科技感背景卡片 */}
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/50 p-6 md:p-8 lg:p-12 shadow-xl shadow-white/10 relative overflow-hidden">
                    {/* 科技感几何装饰 */}
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-full translate-x-4 -translate-y-4"></div>
                    <div className="absolute bottom-0 left-0 w-12 h-24 bg-gradient-to-t from-blue-400/15 to-transparent transform -rotate-12 -translate-x-4 translate-y-8 rounded"></div>
                    <div className="absolute top-1/3 right-8 w-1 h-16 bg-gradient-to-b from-yellow-400/40 to-transparent"></div>
                    
                    <div className="relative z-10">
                      <h1 className="text-3xl md:text-4xl lg:text-6xl font-black leading-tight mb-4 md:mb-6 font-comfortaa">
                        <span className="block drop-shadow-lg">Every Child Deserves</span>
                        <span className="text-yellow-400 drop-shadow-lg">A Clear Path to Success</span>
                      </h1>
                      
                      {/* 科技感副标题 */}
                      <div className="mb-6 md:mb-8 relative">
                        <div className="w-20 h-0.5 bg-gradient-to-r from-yellow-400 to-yellow-300 mb-4 rounded-full"></div>
                        <p className="text-base md:text-lg lg:text-xl text-white/95 leading-relaxed drop-shadow-md font-semibold">
                          AI-powered reports that turn confusion into clarity in minutes
                        </p>
                      </div>

                      {/* 三步式解释 - 新增 */}
                      <div className="mb-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                          <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mb-2 relative">
                              <span className="text-black font-black text-lg">1</span>
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping"></div>
                            </div>
                            <p className="text-white font-bold text-sm uppercase tracking-wider">Upload Info</p>
                          </div>
                          <div className="hidden md:block w-16 h-0.5 bg-gradient-to-r from-yellow-400/60 to-cyan-400/60 rounded-full"></div>
                          <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center mb-2 relative">
                              <span className="text-white font-black text-lg">2</span>
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                            </div>
                            <p className="text-white font-bold text-sm uppercase tracking-wider">AI Analysis</p>
                          </div>
                          <div className="hidden md:block w-16 h-0.5 bg-gradient-to-r from-cyan-400/60 to-blue-400/60 rounded-full"></div>
                          <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center mb-2 relative">
                              <span className="text-white font-black text-lg">3</span>
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-bounce"></div>
                            </div>
                            <p className="text-white font-bold text-sm uppercase tracking-wider">Get Plan</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* CTA 按钮组 */}
                      <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Link 
                          to="/dashboard/demo3" 
                          className="inline-flex items-center justify-center px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-black uppercase tracking-wider rounded-xl border border-white/20 backdrop-blur-sm shadow-lg shadow-yellow-500/25 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-yellow-500/30 group text-base md:text-lg"
                        >
                          TRY FREE DEMO
                          <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </Link>
                        <a 
                          href="https://forms.gle/wWA4FyPa1mw2dM9g9"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center px-8 md:px-12 py-4 md:py-5 bg-white/10 backdrop-blur-sm text-white font-black uppercase tracking-wider rounded-xl border-2 border-white/30 transform transition-all duration-300 hover:scale-105 hover:bg-white/20 hover:border-white/50 text-base md:text-lg"
                        >
                          Contact Us
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - 科技感路径可视化 */}
              <div className="absolute top-1/2 right-4 md:right-8 lg:right-16 transform -translate-y-1/2 hidden md:block">
                <div className="relative">
                  {/* 科技感箭头和文字 */}
                  <div className="absolute -top-16 md:-top-20 -right-4">
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-3 md:px-4 py-1 md:py-2 font-black text-xs md:text-sm rounded-lg border border-white/20 backdrop-blur-sm transform -rotate-1">
                      YOUR GOAL ⭐
                    </div>
                    <div className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-yellow-400 mx-auto mt-1"></div>
                  </div>
                  
                  {/* 科技感路径可视化 */}
                  <div className="flex flex-col items-center space-y-2 md:space-y-3">
                    <div className="w-3 h-3 md:w-4 md:h-4 bg-gradient-to-br from-white to-white/80 border border-yellow-400/50 rounded-full backdrop-blur-sm"></div>
                    <div className="w-0.5 h-6 md:h-8 bg-gradient-to-b from-white/80 to-white/40 rounded-full"></div>
                    <div className="w-3 h-3 md:w-4 md:h-4 bg-gradient-to-br from-yellow-400 to-yellow-300 border border-white/50 rounded-full backdrop-blur-sm animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <div className="w-0.5 h-6 md:h-8 bg-gradient-to-b from-yellow-400/80 to-yellow-400/40 rounded-full"></div>
                    <div className="w-3 h-3 md:w-4 md:h-4 bg-gradient-to-br from-white to-white/80 border border-yellow-400/50 rounded-full backdrop-blur-sm animate-pulse" style={{animationDelay: '1s'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* 即时价值展示 - 新增在Features Section之前 */}
        <div className="py-16 bg-white relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-10 right-10 w-12 h-12 bg-gradient-to-br from-yellow-400/30 to-yellow-600/20 rounded-full backdrop-blur-sm animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-6 h-16 bg-gradient-to-t from-blue-600/30 to-cyan-400/20 backdrop-blur-sm transform -rotate-12"></div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-100/80 to-blue-100/80 backdrop-blur-sm rounded-full mb-6 border border-white/50">
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Problems We Solve</span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
                From Confusion to 
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">Clear Action</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {/* BEFORE SDP - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-red-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-red-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-400/20 to-transparent rounded-full -translate-y-4 translate-x-4"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-32 bg-gradient-to-t from-red-100/30 to-transparent transform rotate-45 -translate-x-8 translate-y-8"></div>
                  <div className="absolute top-1/2 left-4 w-1 h-20 bg-gradient-to-b from-red-600/50 to-transparent"></div>
                  
                  {/* 科技感超大符号 */}
                  <div className="absolute top-4 right-4 text-8xl font-black text-red-600/10 leading-none drop-shadow-lg">?!</div>
                  
                  <div className="relative p-8 z-10">
                    {/* 科技感标签徽章 */}
                    <div className="inline-block bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6 rounded-lg border border-white/20 backdrop-blur-sm transform -rotate-1">
                      BEFORE
                    </div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-4xl font-black text-gray-900 mb-2 leading-tight">
                        CHAOS
                        <div className="text-2xl bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent font-black">MODE</div>
                      </div>
                      <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-orange-400 rounded-full"></div>
                    </div>

                    {/* 问题列表 - 科技感设计 */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-red-600 transform rotate-45 mt-1.5 mr-3 flex-shrink-0 animate-pulse"></div>
                        <span className="text-gray-700 font-medium">"My child has potential but I don't know how to help"</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-red-600 transform rotate-45 mt-1.5 mr-3 flex-shrink-0 animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <span className="text-gray-700 font-medium">"University requirements are overwhelming"</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-red-600 transform rotate-45 mt-1.5 mr-3 flex-shrink-0 animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        <span className="text-gray-700 font-medium">"Study strategies aren't working"</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-red-500 to-red-600 transform rotate-45 mt-1.5 mr-3 flex-shrink-0 animate-pulse" style={{animationDelay: '0.6s'}}></div>
                        <span className="text-gray-700 font-medium">"No clear roadmap for success"</span>
                      </div>
                    </div>

                   
                  </div>
                </div>
              </div>

              {/* WITH SDP - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-green-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-green-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 left-0 w-24 h-16 bg-gradient-to-r from-green-200/30 to-transparent transform rotate-12 -translate-x-6 -translate-y-4 rounded"></div>
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-transparent transform -rotate-45 translate-x-6 translate-y-6 rounded"></div>
                  <div className="absolute top-1/3 right-4 w-1 h-24 bg-gradient-to-b from-green-500/50 to-transparent"></div>
                  
                  {/* 科技感超大符号 */}
                  <div className="absolute top-4 right-4 text-8xl font-black text-green-600/10 leading-none drop-shadow-lg">✓</div>
                  
                  <div className="relative p-8 z-10">
                    {/* 科技感标签徽章 */}
                    <div className="inline-block bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6 rounded-lg border border-white/20 backdrop-blur-sm transform rotate-1">
                      WITH SDP
                    </div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-4xl font-black text-gray-900 mb-2 leading-tight">
                        CLEAR
                        <div className="text-2xl bg-gradient-to-r from-green-600 to-blue-500 bg-clip-text text-transparent font-black">PATH</div>
                      </div>
                      <div className="w-16 h-1 bg-gradient-to-r from-green-600 to-blue-400 rounded-full"></div>
                    </div>

                    {/* 解决方案列表 - 科技感设计 */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-green-500 to-green-600 rounded-full mt-1.5 mr-3 flex-shrink-0 relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-500 rounded-full animate-ping"></div>
                        </div>
                        <span className="text-gray-700 font-medium">Precise strength & weakness analysis</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-green-500 to-blue-500 rounded-full mt-1.5 mr-3 flex-shrink-0 relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-blue-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <span className="text-gray-700 font-medium">Step-by-step university preparation</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mt-1.5 mr-3 flex-shrink-0 relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                        </div>
                        <span className="text-gray-700 font-medium">Personalized learning strategies</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-3 h-3 bg-gradient-to-br from-cyan-500 to-yellow-500 rounded-full mt-1.5 mr-3 flex-shrink-0 relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                        </div>
                        <span className="text-gray-700 font-medium">Monthly action checklists</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30 relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-20 right-10 w-16 h-16 bg-gradient-to-br from-yellow-400/30 to-yellow-600/20 rounded-lg backdrop-blur-sm transform rotate-45 animate-pulse"></div>
          <div className="absolute bottom-32 left-10 w-8 h-20 bg-gradient-to-t from-blue-600/30 to-cyan-400/20 backdrop-blur-sm transform -rotate-12"></div>
          <div className="absolute top-1/2 right-20 w-12 h-12 bg-gradient-to-r from-cyan-400/40 to-blue-500/30 rounded-full backdrop-blur-sm animate-bounce" style={{animationDelay: '1s'}}></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100/80 to-cyan-100/80 backdrop-blur-sm rounded-full mb-6 border border-white/50">
                <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Why Choose SDP</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Turn Uncertainty Into
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">Clear Success</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Our AI-powered platform transforms abstract educational goals into concrete action plans, giving every student a personalized roadmap to excellence
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {/* Feature 1 - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-blue-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-blue-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-full -translate-y-8 translate-x-8"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-32 bg-gradient-to-t from-blue-100/30 to-transparent transform rotate-45 -translate-x-8 translate-y-8"></div>
                  <div className="absolute top-1/2 left-4 w-1 h-20 bg-gradient-to-b from-blue-600/50 to-transparent"></div>
                  
                  <div className="relative p-8 lg:p-10 z-10">
                    {/* 科技感超大数字 */}
                    <div className="text-8xl lg:text-9xl font-black text-blue-600/5 absolute top-2 right-4 leading-none drop-shadow-lg">01</div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-3xl font-black text-gray-900 mb-2 relative">
                        AI
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>
                      </div>
                      <div className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent uppercase tracking-widest">DIAGNOSTICS</div>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-blue-600 to-yellow-400 mt-2 rounded-full"></div>
                    </div>

                    {/* 科技感数据可视化 */}
                    <div className="mb-6 flex items-end space-x-1">
                      <div className="w-2 h-8 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"></div>
                      <div className="w-2 h-12 bg-gradient-to-t from-blue-400 to-cyan-400 rounded-t"></div>
                      <div className="w-2 h-6 bg-gradient-to-t from-blue-300 to-cyan-300 rounded-t"></div>
                      <div className="w-2 h-16 bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t animate-pulse"></div>
                      <div className="w-2 h-10 bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t"></div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-6 relative z-10">
                      Advanced algorithms analyze learning patterns through Academic Health Check heat maps and Competitive Edge Scanners
                    </p>

                    {/* 科技感CTA */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3"
                        className="block text-center bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature 2 - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-cyan-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-cyan-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 left-0 w-24 h-12 bg-gradient-to-r from-cyan-200/30 to-transparent transform rotate-12 -translate-x-4 -translate-y-2 rounded"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-transparent transform rotate-45 translate-x-4 translate-y-4 rounded"></div>
                  <div className="absolute top-1/3 right-4 w-0.5 h-24 bg-gradient-to-b from-cyan-500/50 to-transparent"></div>
                  
                  <div className="relative p-8 lg:p-10 z-10">
                    {/* 科技感超大数字 */}
                    <div className="text-8xl lg:text-9xl font-black text-cyan-600/5 absolute top-2 right-4 leading-none drop-shadow-lg">02</div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-3xl font-black text-gray-900 mb-2 relative">
                        MAP
                        <span className="absolute -bottom-1 -right-1 w-3 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500"></span>
                      </div>
                      <div className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-blue-500 bg-clip-text text-transparent uppercase tracking-widest">STRATEGY</div>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-cyan-600 to-yellow-400 mt-2 rounded-full"></div>
                    </div>

                    {/* 科技感路径可视化 */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full animate-pulse"></div>
                        <div className="flex-1 h-0.5 bg-gradient-to-r from-cyan-600/80 to-cyan-300/40 rounded-full"></div>
                        <div className="w-2 h-2 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full"></div>
                        <div className="flex-1 h-0.5 bg-gradient-to-r from-cyan-300/40 to-yellow-400/80 rounded-full"></div>
                        <div className="w-4 h-4 bg-gradient-to-br from-yellow-400 to-yellow-300 transform rotate-45 animate-pulse"></div>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-6 relative z-10">
                      Transform vague dreams into Strategic Activity Maps with Implementation Breakdowns - actionable monthly checklists
                    </p>

                    {/* 科技感CTA */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3"
                        className="block text-center bg-gradient-to-r from-cyan-600 to-blue-500 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature 3 - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-yellow-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-yellow-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-100/30 to-transparent rounded-lg"></div>
                  <div className="absolute bottom-0 left-0 w-12 h-24 bg-gradient-to-t from-yellow-200/30 to-transparent transform -rotate-12 -translate-x-2 translate-y-4 rounded"></div>
                  <div className="absolute top-1/4 left-2 w-0.5 h-16 bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                  
                  <div className="relative p-8 lg:p-10 z-10">
                    {/* 科技感超大数字 */}
                    <div className="text-8xl lg:text-9xl font-black text-yellow-500/5 absolute top-2 right-4 leading-none drop-shadow-lg">03</div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-3xl font-black text-gray-900 mb-2 relative">
                        GROW
                        <span className="absolute -top-2 -right-2 w-2 h-6 bg-gradient-to-t from-yellow-400 to-yellow-300 transform rotate-12 rounded"></span>
                      </div>
                      <div className="text-lg font-bold bg-gradient-to-r from-yellow-600 to-orange-500 bg-clip-text text-transparent uppercase tracking-widest">EVOLUTION</div>
                      <div className="w-12 h-0.5 bg-gradient-to-r from-yellow-600 to-blue-400 mt-2 rounded-full"></div>
                    </div>

                    {/* 科技感增长图表 */}
                    <div className="mb-6">
                      <div className="flex items-end space-x-1 h-12">
                        <div className="w-3 h-3 bg-gradient-to-t from-yellow-600 to-yellow-500 rounded-t"></div>
                        <div className="w-3 h-5 bg-gradient-to-t from-yellow-500 to-yellow-400 rounded-t"></div>
                        <div className="w-3 h-7 bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t"></div>
                        <div className="w-3 h-9 bg-gradient-to-t from-yellow-300 to-orange-300 rounded-t"></div>
                        <div className="w-3 h-12 bg-gradient-to-t from-yellow-500 to-blue-400 rounded-t animate-pulse"></div>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-6 relative z-10">
                      Our system builds a personalized database that grows with your child, making each analysis more insightful over time
                    </p>

                    {/* 科技感CTA */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3"
                        className="block text-center bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 社会证明 - 新增在Report Templates Section之后 */}
        <div className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50/30 relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-10 left-10 w-14 h-14 bg-gradient-to-br from-blue-400/30 to-blue-600/20 rounded-lg backdrop-blur-sm transform rotate-45 animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-8 h-16 bg-gradient-to-t from-yellow-400/30 to-transparent backdrop-blur-sm transform -rotate-12"></div>
          <div className="absolute top-1/2 right-1/4 w-10 h-10 bg-gradient-to-r from-cyan-400/40 to-blue-500/30 rounded-full backdrop-blur-sm animate-bounce"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100/80 to-cyan-100/80 backdrop-blur-sm rounded-full mb-6 border border-white/50">
                <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Success Stories</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Real Results from
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">Real Families</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* 用户评价 1 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-blue-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-blue-500/20 overflow-hidden p-8">
                  {/* 科技感装饰 */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-full -translate-y-4 translate-x-4"></div>
                  <div className="absolute bottom-0 left-0 w-12 h-20 bg-gradient-to-t from-blue-100/30 to-transparent transform rotate-12 -translate-x-4 translate-y-6"></div>
                  
                  <div className="relative z-10">
                    {/* 星级评分 */}
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    
                    <blockquote className="text-gray-700 mb-6 leading-relaxed font-medium">
                      "SDP transformed our daughter's university preparation. The precise analysis showed exactly what she needed to improve, and the monthly action plan made everything manageable."
                    </blockquote>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-bold text-lg">S</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Sarah Chen</p>
                        <p className="text-sm text-gray-600">Parent, Grade 11</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 用户评价 2 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-cyan-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-cyan-500/20 overflow-hidden p-8">
                  {/* 科技感装饰 */}
                  <div className="absolute top-0 left-0 w-20 h-12 bg-gradient-to-r from-cyan-200/30 to-transparent transform rotate-12 -translate-x-6 -translate-y-2"></div>
                  <div className="absolute bottom-0 right-0 w-14 h-14 bg-gradient-to-br from-yellow-400/20 to-transparent transform rotate-45 translate-x-4 translate-y-4"></div>
                  
                  <div className="relative z-10">
                    {/* 星级评分 */}
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    
                    <blockquote className="text-gray-700 mb-6 leading-relaxed font-medium">
                      "Finally understood my learning style! The daily learning optimization report helped me study smarter, not harder. My grades improved dramatically."
                    </blockquote>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-bold text-lg">M</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Marcus Johnson</p>
                        <p className="text-sm text-gray-600">Student, Grade 10</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 用户评价 3 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-yellow-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-yellow-500/20 overflow-hidden p-8">
                  {/* 科技感装饰 */}
                  <div className="absolute top-0 right-0 w-18 h-16 bg-gradient-to-br from-blue-100/30 to-transparent rounded-lg -translate-y-2 translate-x-2"></div>
                  <div className="absolute bottom-0 left-0 w-10 h-18 bg-gradient-to-t from-yellow-200/30 to-transparent transform -rotate-12 -translate-x-2 translate-y-4"></div>
                  
                  <div className="relative z-10">
                    {/* 星级评分 */}
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    
                    <blockquote className="text-gray-700 mb-6 leading-relaxed font-medium">
                      "As a parent of a young learner, SDP helped us discover our son's multiple intelligences early. The personalized exploration plan is genius!"
                    </blockquote>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-600 to-yellow-400 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-bold text-lg">L</span>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Lisa Rodriguez</p>
                        <p className="text-sm text-gray-600">Parent, Grade 3</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 合作学校展示 - 新增 */}
        <div className="py-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-10 right-10 w-12 h-12 bg-gradient-to-br from-blue-400/30 to-blue-600/20 rounded-lg backdrop-blur-sm transform rotate-45 animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-6 h-16 bg-gradient-to-t from-yellow-400/30 to-transparent backdrop-blur-sm transform -rotate-12"></div>
          <div className="absolute top-1/2 left-1/4 w-8 h-8 bg-gradient-to-r from-cyan-400/40 to-blue-500/30 rounded-full backdrop-blur-sm animate-bounce"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100/80 to-cyan-100/80 backdrop-blur-sm rounded-full mb-6 border border-white/50">
                <span className="text-sm font-semibold text-blue-700 uppercase tracking-wider">Educational Innovation</span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
                Pioneering the Future of
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">Personalized Education</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Building next-generation AI-powered educational solutions to transform how students learn and achieve their goals
              </p>
            </div>

            {/* 技术展示 */}
            <div className="relative">
              <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/50 p-8 md:p-12 relative overflow-hidden shadow-xl">
                {/* 科技感装饰背景 */}
                <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full -translate-x-6 -translate-y-6"></div>
                <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-br from-yellow-400/10 to-transparent transform rotate-45 translate-x-4 translate-y-4"></div>
                <div className="absolute top-1/2 right-0 w-1 h-32 bg-gradient-to-b from-cyan-500/20 to-transparent"></div>
                <div className="absolute bottom-1/4 left-0 w-1 h-20 bg-gradient-to-t from-blue-600/20 to-transparent"></div>
                
                <div className="relative z-10">
                  {/* 技术愿景图片 */}
                  <div className="flex justify-center mb-8">
                    <img 
                      src="https://www.all-as.com/_nuxt/img/cooperation.4990c6d.png" 
                      alt="AI Educational Technology" 
                      className="max-w-full h-auto rounded-lg shadow-lg"
                      style={{ maxHeight: '200px', objectFit: 'contain' }}
                    />
                  </div>
                  
                  {/* 技术特色展示 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="text-center p-4 bg-white/30 backdrop-blur-sm rounded-xl border border-white/30 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full -translate-y-2 translate-x-2"></div>
                      <div className="relative z-10">
                        <div className="text-2xl font-black text-blue-600 mb-2">AI</div>
                        <div className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Powered Analysis</div>
                      </div>
                    </div>
                    <div className="text-center p-4 bg-white/30 backdrop-blur-sm rounded-xl border border-white/30 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-6 h-12 bg-gradient-to-t from-cyan-400/20 to-transparent transform -rotate-12 -translate-x-2 -translate-y-2"></div>
                      <div className="relative z-10">
                        <div className="text-2xl font-black text-cyan-600 mb-2">24/7</div>
                        <div className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Smart Support</div>
                      </div>
                    </div>
                    <div className="text-center p-4 bg-white/30 backdrop-blur-sm rounded-xl border border-white/30 relative overflow-hidden">
                      <div className="absolute bottom-0 right-0 w-8 h-8 bg-gradient-to-br from-yellow-400/20 to-transparent transform rotate-45 translate-x-2 translate-y-2"></div>
                      <div className="relative z-10">
                        <div className="text-2xl font-black text-yellow-600 mb-2">∞</div>
                        <div className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Continuous Growth</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Report Templates Section */}
        <div id="programs" className="py-20 bg-white relative overflow-hidden">
          {/* 科技感背景装饰元素 */}
          <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-blue-100/40 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-50 -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-r from-yellow-100/40 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-50 translate-x-1/2 translate-y-1/2"></div>
          
          {/* 科技感装饰元素 */}
          <div className="absolute top-10 right-10 w-16 h-16 bg-gradient-to-br from-yellow-400/30 to-yellow-600/20 rounded-lg backdrop-blur-sm transform rotate-45 animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-8 h-16 bg-gradient-to-t from-blue-600/30 to-cyan-400/20 backdrop-blur-sm transform -rotate-12"></div>
          <div className="absolute top-1/3 left-1/4 w-12 h-12 bg-gradient-to-r from-cyan-400/40 to-blue-500/30 rounded-full backdrop-blur-sm animate-bounce"></div>
          <div className="absolute bottom-1/4 right-1/3 w-6 h-20 bg-gradient-to-t from-yellow-500/30 to-transparent backdrop-blur-sm transform rotate-12"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100/80 to-yellow-100/80 backdrop-blur-sm rounded-full mb-6 border border-white/50">
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Three Report Categories</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Turn Ordinary Education Into
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">Extraordinary Success</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Discover our specialized report categories that provide GPS-like guidance, turning confusion into clear next steps for every stage of your child's educational journey
              </p>
            </div>

            <div className="grid gap-8 grid-cols-1 lg:grid-cols-3">
              {/* University Applications Report - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-blue-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-blue-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 left-0 w-32 h-16 bg-gradient-to-r from-blue-400/20 to-blue-600/10 transform -rotate-12 -translate-x-8 -translate-y-4 rounded"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-full translate-x-8 translate-y-8"></div>
                  <div className="absolute top-1/2 left-0 w-2 h-32 bg-gradient-to-b from-blue-100/50 to-transparent transform rotate-45 -translate-x-2"></div>
                  
                  {/* 科技感超大数字 */}
                  <div className="absolute top-4 right-4 text-7xl font-black text-blue-600/10 leading-none drop-shadow-lg">95%</div>
                  
                  <div className="relative p-8 z-10">
                    {/* 科技感标签徽章 */}
                    <div className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6 rounded-lg border border-white/20 backdrop-blur-sm transform -rotate-1">
                      UNIVERSITY
                    </div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-4xl font-black text-gray-900 mb-2 leading-tight">
                        PRECISE
                        <div className="text-2xl bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent font-black">MATCHING</div>
                      </div>
                      <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 rounded-full"></div>
                    </div>

                    {/* 科技感数据可视化 - 录取率 */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-gray-600 uppercase">ACCEPTANCE RATE</span>
                      </div>
                      <div className="grid grid-cols-10 gap-1">
                        {Array.from({length: 10}, (_, i) => (
                          <div key={i} className={`h-3 rounded ${i < 7 ? 'bg-gradient-to-t from-blue-600 to-blue-400' : i < 9 ? 'bg-gradient-to-t from-blue-400 to-blue-300' : 'bg-gray-200'}`}></div>
                        ))}
                      </div>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      We precisely map your child's unique qualities to what top universities seek, including competitive edge scanning and implementation strategies
                    </p>

                    {/* 科技感功能列表 */}
                    <div className="mb-8 space-y-2">
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full mr-3"></div>
                        <span className="font-semibold">University Requirements Mapping</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-yellow-400 to-yellow-300 rounded-full mr-3"></div>
                        <span className="font-semibold">Competitive Edge Analysis</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Application Strategy Implementation</span>
                      </div>
                    </div>

                    {/* 科技感CTA按钮 */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3"
                        className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 font-bold uppercase tracking-wider rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Learning Report - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-cyan-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-cyan-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 right-0 w-28 h-20 bg-gradient-to-r from-cyan-200/30 to-transparent transform rotate-12 translate-x-8 -translate-y-4 rounded"></div>
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-transparent transform -rotate-45 -translate-x-4 translate-y-4 rounded"></div>
                  <div className="absolute top-1/3 right-0 w-1 h-24 bg-gradient-to-b from-cyan-500/50 to-transparent"></div>
                  
                  <div className="relative p-8 z-10">                    
                    {/* 科技感标签徽章 */}
                    <div className="inline-block bg-gradient-to-r from-cyan-600 to-cyan-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6 rounded-lg border border-white/20 backdrop-blur-sm transform rotate-1">
                      DAILY LEARNING
                    </div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-4xl font-black text-gray-900 mb-2 leading-tight">
                        SMART
                        <div className="text-2xl bg-gradient-to-r from-cyan-600 to-blue-500 bg-clip-text text-transparent font-black">OPTIMIZATION</div>
                      </div>
                      <div className="w-16 h-1 bg-gradient-to-r from-cyan-600 to-yellow-400 rounded-full"></div>
                    </div>

                    {/* 科技感学习效率可视化 */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-gray-600 uppercase">EFFICIENCY BOOST</span>
                      </div>
                      <div className="flex items-end space-x-1 h-8">
                        <div className="w-4 h-3 bg-gradient-to-t from-cyan-600 to-cyan-500 rounded-t"></div>
                        <div className="w-4 h-5 bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t"></div>
                        <div className="w-4 h-7 bg-gradient-to-t from-cyan-400 to-cyan-300 rounded-t"></div>
                        <div className="w-4 h-8 bg-gradient-to-t from-cyan-600 to-yellow-400 rounded-t animate-pulse"></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">BEFORE → AFTER</div>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      We decode specific learning patterns and turn homework struggles into productive study sessions with personalized strategies
                    </p>

                    {/* 科技感功能列表 */}
                    <div className="mb-8 space-y-2">
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Learning Style Analysis</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-yellow-400 to-yellow-300 rounded-full mr-3"></div>
                        <span className="font-semibold">Study Strategy Optimization</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-cyan-400 to-blue-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Performance Tracking</span>
                      </div>
                    </div>

                    {/* 科技感CTA按钮 */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3/academic" 
                        className="block w-full text-center bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-4 px-6 font-bold uppercase tracking-wider rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Young Learners Report - 科技Memphis风格 */}
              <div className="group relative">
                <div className="relative bg-white/80 backdrop-blur-lg rounded-2xl border border-white/50 shadow-xl shadow-yellow-500/10 transform transition duration-500 hover:-translate-y-3 hover:shadow-2xl hover:shadow-yellow-500/20 overflow-hidden">
                  {/* 科技感几何背景 */}
                  <div className="absolute top-0 left-0 w-20 h-28 bg-gradient-to-t from-yellow-200/30 to-transparent transform -rotate-12 -translate-x-6 -translate-y-6 rounded"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full translate-x-4 translate-y-4"></div>
                  <div className="absolute top-1/4 left-0 w-1 h-20 bg-gradient-to-b from-yellow-500/50 to-transparent"></div>
                  
                  <div className="relative p-8 z-10">                    
                    {/* 科技感标签徽章 */}
                    <div className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest mb-6 rounded-lg border border-white/20 backdrop-blur-sm transform -rotate-1">
                      YOUNG LEARNERS
                    </div>
                    
                    {/* 科技感标题 */}
                    <div className="mb-6">
                      <div className="text-4xl font-black text-gray-900 mb-2 leading-tight">
                        INTEREST
                        <div className="text-2xl bg-gradient-to-r from-yellow-600 to-orange-500 bg-clip-text text-transparent font-black">DISCOVERY</div>
                      </div>
                      <div className="w-16 h-1 bg-gradient-to-r from-yellow-600 to-blue-400 rounded-full"></div>
                    </div>

                    {/* 科技感多元智能可视化 */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-gray-600 uppercase">TALENT AREAS</span>
                      </div>
                      <div className="grid grid-cols-6 gap-1">
                        <div className="h-4 bg-gradient-to-t from-yellow-600 to-yellow-500 rounded"></div>
                        <div className="h-4 bg-gradient-to-t from-yellow-500 to-yellow-400 rounded"></div>
                        <div className="h-4 bg-gradient-to-t from-yellow-400 to-yellow-300 rounded"></div>
                        <div className="h-4 bg-gradient-to-t from-blue-400 to-blue-300 rounded"></div>
                        <div className="h-4 bg-gradient-to-t from-yellow-300 to-orange-300 rounded"></div>
                        <div className="h-4 bg-gradient-to-t from-blue-300 to-cyan-300 rounded"></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">MULTIPLE INTELLIGENCES</div>
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed">
                      For our youngest learners, we focus on interest cultivation and diverse ability development through personalized exploration
                    </p>

                    {/* 科技感功能列表 */}
                    <div className="mb-8 space-y-2">
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-yellow-600 to-yellow-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Interest Discovery</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Multi-Ability Development</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full mr-3"></div>
                        <span className="font-semibold">Personalized Exploration</span>
                      </div>
                    </div>

                    {/* 科技感CTA按钮 */}
                    <div className="relative">
                      <Link 
                        to="/dashboard/demo3" 
                        className="block w-full text-center bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-4 px-6 font-bold uppercase tracking-wider rounded-xl backdrop-blur-sm border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/25"
                      >
                        TRY FREE DEMO
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section - 科技Memphis风格 */}
        <div className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 rounded-lg backdrop-blur-sm transform rotate-45 animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-16 h-32 bg-gradient-to-t from-white/10 to-transparent backdrop-blur-sm transform -rotate-12"></div>
          <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-gradient-to-r from-yellow-400/20 to-transparent rounded-full backdrop-blur-sm animate-bounce"></div>
          <div className="absolute bottom-1/4 right-1/3 w-8 h-20 bg-gradient-to-t from-white/15 to-transparent backdrop-blur-sm transform rotate-12"></div>
          
          {/* 科技感超大背景字符 */}
          <div className="absolute top-8 right-8 text-9xl font-black text-white/5 leading-none drop-shadow-lg">AI</div>
          
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
            {/* 科技感标签徽章 */}
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 font-black uppercase tracking-widest mb-8 rounded-lg border border-white/20 backdrop-blur-sm transform -rotate-1">
              START NOW
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 uppercase tracking-wide">
              START YOUR EDUCATIONAL
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-300">JOURNEY TODAY</span>
            </h2>
            
            {/* 科技感几何装饰 */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-300 mr-4 rounded-full"></div>
              <div className="w-8 h-8 bg-gradient-to-br from-white to-white/80 transform rotate-45 rounded"></div>
              <div className="w-24 h-1 bg-gradient-to-l from-yellow-400 to-yellow-300 ml-4 rounded-full"></div>
            </div>
            
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed font-semibold">
              Experience the power of AI-driven education technology and unlock your full academic potential with personalized insights and guidance
            </p>
            
            <div className="flex justify-center">
              <a 
                href="https://forms.gle/wWA4FyPa1mw2dM9g9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-4 text-lg font-black text-white bg-white/10 backdrop-blur-sm uppercase tracking-wider rounded-xl border-2 border-white/30 transform transition-all duration-300 hover:scale-105 hover:bg-white/20 hover:border-white/50"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Footer - 科技Memphis风格 */}
        <footer className="bg-gradient-to-br from-gray-50 to-blue-50/30 border-t-2 border-blue-600/20 relative overflow-hidden">
          {/* 科技感装饰元素 */}
          <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-blue-600/10 to-transparent rounded-lg transform rotate-45 -translate-x-4 -translate-y-4"></div>
          <div className="absolute bottom-0 right-0 w-12 h-24 bg-gradient-to-t from-yellow-400/20 to-transparent backdrop-blur-sm transform -rotate-12 translate-x-4 translate-y-8"></div>
          <div className="absolute top-1/2 left-1/3 w-8 h-8 bg-gradient-to-r from-cyan-400/20 to-transparent rounded-full backdrop-blur-sm"></div>
          <div className="absolute bottom-1/4 left-20 w-1 h-16 bg-gradient-to-b from-blue-600/30 to-transparent transform rotate-12"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  {/* 科技感logo容器 */}
                  <div className="relative">
                    <img 
                      src="https://canada-public.oss-cn-shenzhen.aliyuncs.com/pub_20250529/d9205bc755c6427f9a87e2280c7b8f98.png" 
                      alt="SDP Logo" 
                      className="w-10 h-10 object-contain border border-blue-600/30 rounded-lg backdrop-blur-sm"
                    />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-yellow-400 to-yellow-300 rounded-full"></div>
                  </div>
                  <div>
                    <span className="text-2xl font-black tracking-tight" style={{color: '#003366'}}>SDP</span>
                    <div className="text-xs font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent uppercase tracking-widest">AlphaEdu.AI</div>
                  </div>
                </div>
                
                {/* 科技感描述 */}
                <div className="mb-6">
                  <div className="w-12 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 mb-3 rounded-full"></div>
                  <p className="text-gray-700 max-w-md leading-relaxed font-medium">
                    Dedicated to providing personalized, scientific educational solutions for every student. Using AI technology to illuminate the path of intelligent learning.
                  </p>
                </div>
              </div>
              
              <div>
                <div className="mb-6">
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-2">PRODUCTS</h3>
                  <div className="w-8 h-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 mb-4 rounded-full"></div>
                </div>
                <ul className="space-y-3">
                  <li>
                    <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors font-medium relative group">
                      Student Profiles
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors font-medium relative group">
                      Subject Analysis
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors font-medium relative group">
                      Planning Reports
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="mb-6">
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider mb-2">SUPPORT</h3>
                  <div className="w-8 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 mb-4 rounded-full"></div>
                </div>
                <ul className="space-y-3">
                  <li>
                    <a href="#" className="text-gray-600 hover:text-cyan-600 transition-colors font-medium relative group">
                      Help Center
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-600 to-blue-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-gray-600 hover:text-cyan-600 transition-colors font-medium relative group">
                      Contact Us
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-600 to-blue-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-gray-600 hover:text-cyan-600 transition-colors font-medium relative group">
                      Privacy Policy
                      <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-600 to-blue-500 group-hover:w-full transition-all duration-300 rounded-full"></div>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* 科技感footer底部 */}
            <div className="mt-12 pt-8 border-t border-blue-600/20 relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-300 transform rotate-45 rounded"></div>
              <div className="text-center">
                <div className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 font-black text-sm uppercase tracking-wider transform -rotate-1 mb-4 rounded-lg border border-white/20 backdrop-blur-sm">
                  {new Date().getFullYear()} SDP
                </div>
                <p className="text-gray-500 font-medium">
                  by AlphaEdu.AI. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>


      </div>
    </>
  );
};

export default LandingPage;
